import React, { useEffect, useRef, useState } from 'react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import Cover from 'components/common/Cover'
import Layout from 'components/common/Layout'
import PaginatedList from 'components/artistsPage/PaginatedList'
import Search from 'components/artistsPage/Search'

import { LIMIT_DESKTOP, LIMIT_PHONE } from 'constants/limits'
import threshold from 'constants/threshold'
import { tabsIndexes } from 'constants/artists'
import RoutesEnum from 'constants/routes'

import useWindowSize from 'hooks/useWindowSize'
import useGetArtists from 'hooks/useGetArtists'

import * as SC from 'styles/artistsStyles'


const ArtistsPage = () => {
  const [limit, setLimit] = useState<number>(LIMIT_DESKTOP)
  const [offset, setOffset] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [tabIndex, setTabIndex] = useState(0)

  const artists = useGetArtists({ search: searchValue, finalists: false, limit, offset })
  const finalists = useGetArtists({ search: searchValue, finalists: true, limit, offset })
  const windowSize = useWindowSize()
  const { push, query } = useRouter()

  const onChangeQueryParam = (queryParam: string) => {
    push(`${RoutesEnum.PAGE_ARTISTS}?tab=${queryParam}`, undefined, { scroll: false })
    setOffset(0)
  }

  useEffect(() => {
    if (
      query.tab &&
      !Array.isArray(query.tab) &&
      Object.keys(tabsIndexes).includes(query.tab)
      // && tabsIndexes[query.tab] !== undefined)
      // Если надо, чтобы при обновлении страницы с табом артистов не убирался квери параметр
    ) {
      setTabIndex(tabsIndexes[query.tab])
    } else {
      setTabIndex(0)
      push(RoutesEnum.PAGE_ARTISTS)
    }
  }, [])

  const onSearch = () => {
    setSearchValue(inputValue)
  }

  const onClear = () => {
    setInputValue('')
    setSearchValue('')
  }

  const paginateRef = useRef(null)
  const executeScroll = () => paginateRef.current.scrollIntoView()

  if (windowSize.width > parseInt(threshold.phone) && limit === LIMIT_PHONE) {
    setLimit(LIMIT_DESKTOP)
  }

  if (windowSize.width < parseInt(threshold.phone) && limit === LIMIT_DESKTOP) {
    setLimit(LIMIT_PHONE)
  }

  return (
    <Layout >
      <>
        <NextSeo
          title="Участники"
          description="Участвуй в творческом акселераторе, прокачай скиллы и получи деньги на свой проект."
          canonical={`https://newopen.su/artists`}
          openGraph={{
            url: 'https://newopen.su/artists',
            title: 'Участники | NEW / OPEN',
            description: 'Участвуй в творческом акселераторе, прокачай скиллы и получи деньги на свой проект.',
            images: [
              {
                url: '/images/artistsPage/cover1.png',
                width: 800,
                height: 600,
                alt: 'Artists page'
              }
            ]
          }}
        />
        <BreadcrumbJsonLd
          itemListElements={[
            {
              position: 1,
              name: 'Главная',
              item: 'https://newopen.su/'
            },
            {
              position: 2,
              name: 'Участники',
              item: 'https://newopen.su/artists'
            }
          ]}
        />
        <SC.ArtistsPageStyled>
          <Cover
            src="/images/artistsPage/cover1.png"
            srcMobile="/images/artistsPage/cover-mobile1.png"
            pageData={{ type: 'commonPage', hasNewOpenText: true, title: 'Участники' }}
          />
          <Tabs
            className="tabs"
            selectedTabClassName="tab_selected"
            selectedIndex={tabIndex}
            onSelect={index => {
              setTabIndex(index)
            }}
          >
            <a ref={paginateRef}></a>
            <TabList className="tab-list">
              <Tab className="tab" onClick={() => onChangeQueryParam('artists')}>
                <span className="tab-name">Участники</span>
              </Tab>
              <Tab className="tab" onClick={() => onChangeQueryParam('finalists')}>
                <span className="tab-name">Финалисты</span>
              </Tab>
            </TabList>
            <Search
              onChange={e => setInputValue(e.target.value)}
              clearInput={onClear}
              value={inputValue}
              onSearch={onSearch}
            />

            <TabPanel>
              {artists.isSuccess && !artists.isLoading && !artists?.data?.items.length && (
                <SC.DataWrapper>Не найдено</SC.DataWrapper>
              )}

              {artists.isSuccess && artists?.data?.items.length ? (
                <PaginatedList
                  onClear={() => setInputValue('')}
                  data={artists?.data?.items}
                  limit={limit}
                  setOffset={setOffset}
                  artistsCount={artists?.data?.count}
                  offset={offset}
                  scrollToElement={executeScroll}
                />
              ) : (
                ''
              )}

              {artists.isLoading && (
                <SC.DataWrapper>
                  <SC.LoadingIcon src="/svgs/loader.svg" />
                </SC.DataWrapper>
              )}

              {artists.isError && <SC.DataWrapper>Ошибка получения участников, попробуйте позже</SC.DataWrapper>}
            </TabPanel>
            <TabPanel>
              {finalists.isSuccess && !finalists?.data?.items.length && <SC.DataWrapper>Не найдено</SC.DataWrapper>}

              {finalists.isSuccess && finalists?.data?.items.length ? (
                <PaginatedList
                  onClear={() => setInputValue('')}
                  data={finalists?.data?.items}
                  limit={limit}
                  setOffset={setOffset}
                  artistsCount={finalists?.data?.count}
                  offset={offset}
                  scrollToElement={executeScroll}
                />
              ) : (
                ''
              )}

              {finalists.isLoading && (
                <SC.DataWrapper>
                  <SC.LoadingIcon src="/svgs/loader.svg" />
                </SC.DataWrapper>
              )}

              {finalists.isError && <SC.DataWrapper>Ошибка получения финалистов, попробуйте позже</SC.DataWrapper>}
            </TabPanel>
          </Tabs>
        </SC.ArtistsPageStyled>
      </>
    </Layout>
  )
}

export default ArtistsPage
